import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null); // 'admin' | 'editor' | null
    const [loading, setLoading] = useState(true);

    async function signup(email, password) {
        // 1. Check if email is in 'allowed_users' collection
        const userRef = doc(db, 'allowed_users', email); // Using email as ID for simple lookup
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
            throw new Error("This email is not authorized by Admin.");
        }

        const role = userSnap.data().role;

        // 2. Create Auth Account
        const result = await createUserWithEmailAndPassword(auth, email, password);

        // 3. Save user profile with role
        await setDoc(doc(db, 'users', result.user.uid), {
            email,
            role,
            createdAt: new Date().toISOString()
        });

        return result;
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                // Fetch Role
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setUserRole(docSnap.data().role);
                } else {
                    // Fallback or legacy handling
                    setUserRole('guest');
                }
            } else {
                setUserRole(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userRole,
        signup,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
